import React, { useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { fullscreenVODPageTestIDs, pictureDetailedVODPageTestIDs, Theme } from '@constants';
import { useContextReference } from '@hooks';
import { navigationService } from '@services';

import { isAndroid } from '../../../helpers/Platform';
import { FocusableFlatList } from '../../focusable/FocusableFlatList/FocusableFlatList';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { OnDemandEpisodeLayout, OnDemandEpisodeTile } from '../../tiles/OnDemandEpisodeTile/OnDemandEpisodeTile';

const CONSTANTS = {
  height: OnDemandEpisodeLayout.TILE_HEIGHT,
  width: OnDemandEpisodeLayout.TILE_WIDTH,
  spacing: OnDemandEpisodeLayout.TILE_SPACING,
  episodeMaxDescriptionLines: 2,
  rowOffset: Theme.px(90),
};

export const PictureDetailedEpisodesRow = ({
  data,
  onLayoutFocus,
  nextFocusDown,
  nextFocusUp,
  testID,
  onFocus,
  onBlur,
  focusIndexChanged,
  currentEpisode,
  onFlatlistRef,
  onListFocus,
  hideFocus,
  playlist,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const currentEpisodeInfo = data?.episodes?.[currentEpisode] ?? {};

  const episodeReference = useContextReference(pictureDetailedVODPageTestIDs.episodesTiles);

  const nextFocusLeft = isAndroid ? episodeReference : null;
  const nextFocusRight = isAndroid ? episodeReference : null;

  const _getItemLayout = useCallback(
    (_, index) => ({
      length: CONSTANTS.width,
      offset: (CONSTANTS.width + CONSTANTS.spacing) * index,
      index,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const _renderItem = useCallback(
    (_, index) => {
      const rowData = data?.episodes[index];

      return (
        <OnDemandEpisodeTile
          data={{ ...rowData, index, length: data?.episodes?.length ?? 0 }}
          hideFocus={hideFocus}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          setIsItemFocused={focus => {
            setIsFocused(focus);
            onListFocus(focus);
          }}
          testID={`picture_page_item_${index}`}
          onBlur={onBlur}
          onFocus={onFocus}
          onPressPlay={() => {
            navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
              videoID: rowData.id,
              seasonID: data?.id,
              playlist,
              playlistSeasonIndex: rowData.season - 1,
              playlistEpisodeIndex: index,
              needsBookmarking: true,
              startPosition: rowData?.point,
            });
          }}
          onPressRestart={() => {
            navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
              playlist,
              playlistSeasonIndex: rowData.season - 1,
              playlistEpisodeIndex: index,
              videoID: rowData.id,
              seasonID: data?.id,
              needsBookmarking: true,
            });
          }}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, currentEpisode, hideFocus],
  );

  const _keyExtractor = useCallback((_, index) => `pdp_episode_item_${index + 1}`, []);

  const _separator = useCallback(() => <View style={styles.listSeparator} />, []);

  return (
    <>
      <View style={styles.listWrapper}>
        <FocusableFlatList
          containerStyle
          ItemSeparatorComponent={_separator}
          ListFooterComponent={View}
          ListFooterComponentStyle={styles.listFooter}
          data={data?.episodes ?? []}
          focusIndexChanged={focusIndexChanged}
          focusPositions={1}
          getItemLayout={_getItemLayout}
          horizontal
          itemUnderlayColor={'clear'}
          keyExtractor={_keyExtractor}
          listGainedFocus={onLayoutFocus}
          nestedScrollEnabled
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={nextFocusUp}
          paddingStart={CONSTANTS.rowOffset}
          renderItem={_renderItem}
          showsHorizontalScrollIndicator={false}
          testID={testID}
          wrapItem={false}
          onFocus={onFocus}
          onRef={onFlatlistRef}
        />
      </View>
      {isFocused && (
        <View style={styles.descriptionWrapper}>
          <TWCText noTestID style={styles.episodeTitle} variant={'mediumText42'}>
            {currentEpisodeInfo.title ?? ''}
          </TWCText>
          <TWCText
            noTestID
            numberOfLines={CONSTANTS.episodeMaxDescriptionLines}
            style={styles.episodeDescription}
            variant={'regularText30'}
          >
            {currentEpisodeInfo.description ?? ''}
          </TWCText>
        </View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  descriptionWrapper: {
    flex: 1,
    marginLeft: Theme.px(90),
    marginTop: Theme.px(40),
    width: Theme.px(1106),
  },
  episodeDescription: {
    color: Theme.suvaGrey,
    lineHeight: Theme.px(42),
  },
  episodeTitle: {
    marginBottom: Theme.px(6),
  },
  listFooter: {
    height: CONSTANTS.height,
    width: Theme.px(1920 - CONSTANTS.width),
  },
  listSeparator: {
    height: Theme.px(30),
    width: Theme.px(30),
  },
  listWrapper: {
    height: CONSTANTS.height,
  },
});
